import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import DataTable from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    harmfulWrapper?: InjectableCss;
    harmfulIcon?: IconProps;
    harmfulLabel?: TypographyProps;

    wrapper?: InjectableCss;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCell?: DataTableCellProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },

    wrapper: {
        css: css`
            ${mediaQueriesBP.maxMD} {
                margin: 10px 0 25px;
            }

            ${mediaQueriesBP.minMD} {
                margin: 5px 0 40px;
            }
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    dataTableBody: {
        css: css`
            && {
                border: 0;
            }
        `,
    },
    dataTableCell: {
        css: css`
            && {
                min-height: 42px;
                padding: 9px 10px;
                height: auto;
                border: 0;
                text-wrap: wrap;
                white-space: break-spaces;
            }
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties, content, minimumOrderQty, detail },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
    productInfo,
    // eslint-disable-next-line
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    let containsContent =
        content != null &&
        content.htmlContent != null &&
        content.htmlContent.length > 0 &&
        !content.htmlContent.includes("<body>&nbsp;</body>");
    containsContent = false;

    const outOfStock =
        productInfo.inventory == null ||
        productInfo.inventory?.inventoryAvailabilityDtos == null ||
        productInfo.inventory.inventoryAvailabilityDtos?.length === 0
            ? true
            : productInfo.inventory.inventoryAvailabilityDtos[0].availability == null ||
              productInfo.inventory.inventoryAvailabilityDtos[0].availability?.messageType ===
                  AvailabilityMessageType.OutOfStock;

    const documentsAccordion = renderingDocuments && (
        <ManagedAccordionSection
            title={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                containsContent
                    ? false
                    : ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                      (!renderingSpecifications && !renderingAttributes)
            }
            {...styles.specificationsAccordionSection}
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </ManagedAccordionSection>
    );

    const attributesAccordion = renderingAttributes && (
        <ManagedAccordionSection
            title={translate("Attributes")}
            initialExpanded={
                containsContent ? false : attributesFirst || (!renderingSpecifications && !renderingDocuments)
            }
            data-test-selector="productDetails_specifications_attributes"
            {...styles.specificationsAccordionSection}
        >
            <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
                <DataTable>
                    <DataTableBody {...styles.dataTableBody}>
                        <DataTableRow hidden={true}>
                            <DataTableCell></DataTableCell>
                        </DataTableRow>
                        {brand && (
                            <DataTableRow {...styles.dataTableRow}>
                                <DataTableCell {...styles.dataTableCell} data-test-selector="productDetails_brand">
                                    <Typography
                                        data-test-selector="brand_item"
                                        as="p"
                                        {...styles.attributeTypeValuesText}
                                    >
                                        <Typography
                                            data-test-selector="brand_item_label"
                                            {...styles.attributeTypeLabelText}
                                        >
                                            {translate("Brand")}&nbsp;:&nbsp;
                                        </Typography>
                                        <span data-test-selector="brand_item_value">{brand.name}</span>
                                    </Typography>
                                </DataTableCell>
                            </DataTableRow>
                        )}
                        {attributeTypes &&
                            attributeTypes.map(attributeType => (
                                <DataTableRow {...styles.dataTableRow} key={attributeType.id}>
                                    <DataTableCell
                                        {...styles.dataTableCell}
                                        data-test-selector={`productDetails_attribute_${attributeType.id}`}
                                    >
                                        <Typography
                                            key={attributeType.id.toString()}
                                            data-test-selector="attributes_item"
                                            data-attributetypeid={attributeType.id}
                                            {...styles.attributeTypeValuesText}
                                            as="p"
                                        >
                                            <Typography
                                                data-test-selector="attributes_item_label"
                                                {...styles.attributeTypeLabelText}
                                            >
                                                {attributeType.label || attributeType.name}:&nbsp;
                                            </Typography>
                                            <span data-test-selector="attributes_item_value">
                                                {(attributeType.attributeValues ?? [])
                                                    .map(attributeValue => attributeValue.valueDisplay)
                                                    .join(", ")}
                                            </span>
                                        </Typography>
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </ManagedAccordionSection>
    );

    return (
        <>
            <Accordion
                headingLevel={2}
                data-test-selector="productDetails_specifications"
                {...styles.specificationsAccordion}
            >
                {attributesFirst && attributesAccordion}
                {documentsFirst && documentsAccordion}
                {renderingSpecifications &&
                    specifications?.map((specification, index) => {
                        return (
                            <ManagedAccordionSection
                                initialExpanded={
                                    containsContent
                                        ? false
                                        : index === 0 &&
                                          (!documentsFirst || !renderingDocuments) &&
                                          (!attributesFirst || !renderingAttributes)
                                }
                                key={specification.id.toString()}
                                title={specification.nameDisplay}
                                data-test-selector="productDetails_specifications_specification"
                                {...styles.specificationsAccordionSection}
                            >
                                {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                            </ManagedAccordionSection>
                        );
                    })}
                {!attributesFirst && attributesAccordion}
                {!documentsFirst && documentsAccordion}
            </Accordion>

            {detail && detail.isHazardousGood && (
                <StyledWrapper {...styles.harmfulWrapper}>
                    <IconMemo {...styles.harmfulIcon}></IconMemo>
                    <Typography {...styles.harmfulLabel}>{siteMessage("ProductDetails_HarmfulMessage")}</Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
